import axios from "axios";
import { trackPromise } from "react-promise-tracker";
import { camelToSnake, snakeToCamel, prepareValuesToApi } from "../helpers/format";
import { auth } from "../helpers/firebase";
import { handleErrors } from "./error";

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_API_URL,
    timeout: 40000,
});

axiosInstance.interceptors.request.use(
    async config => {
        const user = auth.currentUser;
        if (user) {
            const accessToken = await user.getIdToken();
            if (accessToken) {
                config.headers["Authorization"] = accessToken;
            }
        }
        if (!config.headers["Content-Type"]) {
            config.headers["Content-Type"] = "application/json";
        }
        if (!config.headers["Accept"]) {
            config.headers["Accept"] = "application/json";
        }
        return config;
    },
    error => {
        Promise.reject(error);
    }
);

axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        if (error.response && error.response.status === 401) {
            const originalRequest = error.config;
            if (!originalRequest._retry)  {
                originalRequest._retry = true;
                const user = auth.currentUser;
                if (user) {
                    const accessToken = await user.getIdToken(true);
                    localStorage.setItem("accessToken", accessToken);
                    axios.defaults.headers.common["Authorization"] = accessToken;
                    return axiosInstance(originalRequest);
                }
            }
        }
        return Promise.reject(error);
    }
);


export const axiosGet = (url, body, callback, showErrors=true, silent=false) => {
    const request = axiosInstance.get(url, {params: camelToSnake(prepareValuesToApi(body))})
        .then(res => {
            if (res) {
                if (res.data.success) {
                    callback(snakeToCamel(res.data.content), 1);
                }
                else {
                    const apiErrors = res.data.errors;
                    if (showErrors) handleErrors(apiErrors);
                    callback(apiErrors, 0);
                }
            }
        })
        .catch(err => {
            const appError = err.message;
            if (showErrors) handleErrors([appError]);
            callback(appError, -1);
        });

    // `silent` skips react-promise-tracker, so background/polling calls (e.g.
    // the MBWAY payment page refreshing its status every few seconds) don't
    // flash the app-wide Loading backdrop on every tick.
    if (!silent) trackPromise(request);
}


export const axiosPost = (url, body, callback, showErrors = true, multipartFormData = null) => {
    const headerMultipartFormData = multipartFormData ? {
        headers: { "Content-Type": "multipart/form-data"}
    } : {};
    trackPromise(
        axiosInstance.post(
            url,
            multipartFormData ? multipartFormData : {},
            { params: camelToSnake(prepareValuesToApi(body)), ...headerMultipartFormData },
        )
        .then(res => {
            if (res.data.success) {
                callback(snakeToCamel(res.data.content), 1);
            }
            else {
                const apiErrors = res.data.errors;
                if (showErrors) handleErrors(apiErrors);
                callback(apiErrors, 0);
            }
        })
        .catch(err => {
            const appError = err.message;
            if (showErrors) handleErrors([appError]);
            callback(appError, -1);
        })
    );
}

export const axiosPut = (url, body, callback, showErrors = true, multipartFormData = null) => {
    const headerMultipartFormData = multipartFormData ? {
        headers: { "Content-Type": "multipart/form-data"}
    } : {};
    trackPromise(
        axiosInstance.put(
            url,
            multipartFormData ? multipartFormData : {},
            { params: camelToSnake(prepareValuesToApi(body)), ...headerMultipartFormData },
        )
        .then(res => {
            if (res.data.success) {
                callback(snakeToCamel(res.data.content), 1);
            }
            else {
                const apiErrors = res.data.errors;
                if (showErrors) handleErrors(apiErrors);
                callback(apiErrors, 0);
            }
        })
        .catch(err => {
            const appError = err.message;
            if (showErrors) handleErrors([appError]);
            callback(appError, -1);
        })
    );
}

export const axiosDelete = (url, params, callback, showErrors=true, multipartFormData = null) => {
    const headerMultipartFormData = multipartFormData ? {
        headers: { "Content-Type": "multipart/form-data"}
    } : {};
    trackPromise(
        axiosInstance.delete(
            url,
            {
                params: camelToSnake(prepareValuesToApi(params)),
                data: multipartFormData ? multipartFormData : {},
                ...headerMultipartFormData
            },
        )
        .then(res => {
            if (res.data.success) {
                callback(snakeToCamel(res.data.content), 1);
            }
            else {
                const apiErrors = res.data.errors;
                if (showErrors) handleErrors(apiErrors);
                callback(apiErrors, 0);
            }
        })
        .catch(err => {
            const appError = err.message;
            if (showErrors) handleErrors([appError], true);
            callback(appError, -1);
        })
    );
}
